import { lazy, Suspense, type ComponentType, type ReactElement } from "react";
import { Spin } from "antd";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { ControlRoomLayout } from "../layout/ControlRoomLayout";
import { LoginPage } from "../../modules/auth/LoginPage";
import { useAuthStore } from "../../stores/authStore";
import { PermissionGate } from "../../shared/components/PermissionGate";
import { OperationsTransactionWorkspace } from "../../shared/components/OperationsTransactionTabs";

const lazyNamed = <T extends ComponentType<any>>(
  loader: () => Promise<Record<string, unknown>>,
  exportName: string,
) => lazy(async () => ({ default: (await loader())[exportName] as T }));

const AttachmentListPage = lazyNamed(() => import("../../modules/attachments/AttachmentListPage"), "AttachmentListPage");
const ConsignmentListPage = lazyNamed(() => import("../../modules/consignments/ConsignmentListPage"), "ConsignmentListPage");
const DashboardPage = lazyNamed(() => import("../../modules/dashboard/DashboardPage"), "DashboardPage");
const BookingReportPage = lazyNamed(() => import("../../modules/reports/BookingReportPage"), "BookingReportPage");
const ManifestEntryPage = lazyNamed(() => import("../../modules/operations/ManifestEntryPage"), "ManifestEntryPage");
const GatePassPage = lazyNamed(() => import("../../modules/operations/GatePassPage"), "GatePassPage");
const ManifestArrivalPage = lazyNamed(() => import("../../modules/arrivals/ManifestArrivalPage"), "ManifestArrivalPage");
const DriverListPage = lazyNamed(() => import("../../modules/drivers/DriverListPage"), "DriverListPage");
const FleetListPage = lazyNamed(() => import("../../modules/fleet/FleetListPage"), "FleetListPage");
const MasterDataPage = lazyNamed(() => import("../../modules/masterdata/MasterDataPage"), "MasterDataPage");
const OperationsPage = lazyNamed(() => import("../../modules/operations/OperationsPage"), "OperationsPage");
const AccountingPage = lazyNamed(() => import("../../modules/accounting/AccountingPage"), "AccountingPage");
const PartiesPage = lazyNamed(() => import("../../modules/parties/PartiesPage"), "PartiesPage");
const TripListPage = lazyNamed(() => import("../../modules/trips/TripListPage"), "TripListPage");
const EWayBillDashboardPage = lazyNamed(() => import("../../modules/ewaybill/EWayBillDashboardPage"), "EWayBillDashboardPage");
const CommissionPage = lazyNamed(() => import("../../modules/commission/CommissionPage"), "CommissionPage");
const LorryHirePage = lazyNamed(() => import("../../modules/lorryhire/LorryHirePage"), "LorryHirePage");
const BiltyStationeryPage = lazyNamed(() => import("../../modules/stationery/BiltyStationeryPage"), "BiltyStationeryPage");
const AuditLogPage = lazyNamed(() => import("../../modules/admin/AuditLogPage"), "AuditLogPage");
const OperationsEnquiryPage = lazyNamed(() => import("../../modules/enquiries/EnquiryPages"), "OperationsEnquiryPage");
const AccountingEnquiryPage = lazyNamed(() => import("../../modules/enquiries/EnquiryPages"), "AccountingEnquiryPage");
const InventoryEnquiryPage = lazyNamed(() => import("../../modules/enquiries/EnquiryPages"), "InventoryEnquiryPage");
const UserManagementPage = lazy(() => import("../../modules/users/UserManagementPage"));
const SettingsPage = lazy(() => import("../../modules/settings/SettingsPage"));

const Shell = () => (
  <ControlRoomLayout>
    <Suspense fallback={<div className="route-loading"><Spin size="large" /></div>}>
      <Outlet />
    </Suspense>
  </ControlRoomLayout>
);

const AuthenticatedShell = () => {
  const accessToken = useAuthStore((x) => x.accessToken);
  const location = useLocation();

  if (!accessToken) {
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}${location.hash}` }} />;
  }
  return <Shell />;
};

const PermissionRoute = ({
  permission,
  sectionName,
  children
}: {
  permission: string;
  sectionName: string;
  children: ReactElement;
}) => (
  <PermissionGate permission={permission} sectionName={sectionName}>
    {children}
  </PermissionGate>
);

const AdminOnlyRoute = ({ children }: { children: ReactElement }) => {
  const roles = useAuthStore((x) => x.user?.roles ?? []);
  const isAdmin = roles.some((role) => {
    const normalized = role.toLowerCase();
    return normalized === "admin" || normalized === "administrator" || normalized === "superadmin";
  });

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

export const AppRoutes = () => {
  const accessToken = useAuthStore((x) => x.accessToken);
  const location = useLocation();
  const loginSearchParams = new URLSearchParams(location.search);
  const requestedReturnUrl = loginSearchParams.get("returnUrl");
  const loginRedirectTarget =
    requestedReturnUrl && requestedReturnUrl.startsWith("/")
      ? requestedReturnUrl
      : "/";

  return (
    <Routes>
      <Route path="/login" element={accessToken ? <Navigate to={loginRedirectTarget} replace /> : <LoginPage />} />
      <Route element={<AuthenticatedShell />}>
        <Route index element={<DashboardPage />} />
        <Route path="/consignments" element={
          <PermissionRoute permission="Consignments.Read" sectionName="Consignment Booking">
            <OperationsTransactionWorkspace tabsInHeader><ConsignmentListPage /></OperationsTransactionWorkspace>
          </PermissionRoute>
        } />
        <Route path="/trips" element={
          <PermissionRoute permission="Transport.Read" sectionName="Trip Planning">
            <TripListPage />
          </PermissionRoute>
        } />
        <Route path="/ewaybill" element={
          <PermissionRoute permission="Transport.Read" sectionName="E-Way Bills">
            <EWayBillDashboardPage />
          </PermissionRoute>
        } />
        <Route path="/fleet" element={
          <PermissionRoute permission="Fleet.Read" sectionName="Fleet Management">
            <FleetListPage />
          </PermissionRoute>
        } />
        <Route path="/drivers" element={
          <PermissionRoute permission="Fleet.Read" sectionName="Driver Management">
            <DriverListPage />
          </PermissionRoute>
        } />
        <Route path="/attachments" element={
          <PermissionRoute permission="Transport.Read" sectionName="Document Vault">
            <AttachmentListPage />
          </PermissionRoute>
        } />
        <Route path="/operations" element={
          <PermissionRoute permission="Transport.Read" sectionName="Operational Utilities">
            <OperationsPage />
          </PermissionRoute>
        } />
        <Route path="/operations/enquiry" element={<PermissionRoute permission="Transport.Read" sectionName="Operations Enquiry"><OperationsEnquiryPage /></PermissionRoute>} />
        <Route path="/manifests" element={
          <PermissionRoute permission="Transport.Read" sectionName="Manifest Entry">
            <OperationsTransactionWorkspace><ManifestEntryPage /></OperationsTransactionWorkspace>
          </PermissionRoute>
        } />
        <Route path="/manifest-arrivals" element={
          <PermissionRoute permission="Transport.Read" sectionName="Manifest Arrival">
            <OperationsTransactionWorkspace><ManifestArrivalPage /></OperationsTransactionWorkspace>
          </PermissionRoute>
        } />
        <Route path="/gate-pass" element={
          <PermissionRoute permission="Transport.Read" sectionName="Gate Pass">
            <OperationsTransactionWorkspace><GatePassPage /></OperationsTransactionWorkspace>
          </PermissionRoute>
        } />
        <Route path="/reports" element={
          <PermissionRoute permission="Transport.Read" sectionName="Reports">
            <BookingReportPage />
          </PermissionRoute>
        } />
        <Route path="/master-data" element={
          <PermissionRoute permission="Transport.Read" sectionName="Master Data">
            <MasterDataPage />
          </PermissionRoute>
        } />
        <Route path="/accounting" element={
          <PermissionRoute permission="Accounting.Read" sectionName="Accounting">
            <AccountingPage />
          </PermissionRoute>
        } />
        <Route path="/accounting/enquiry" element={<PermissionRoute permission="Accounting.Read" sectionName="Accounting Enquiry"><AccountingEnquiryPage /></PermissionRoute>} />
        <Route path="/commission" element={
          <PermissionRoute permission="Accounting.Read" sectionName="Commission">
            <CommissionPage />
          </PermissionRoute>
        } />
        <Route path="/lorry-hire" element={
          <PermissionRoute permission="Fleet.Read" sectionName="Lorry Hire">
            <OperationsTransactionWorkspace><LorryHirePage /></OperationsTransactionWorkspace>
          </PermissionRoute>
        } />
        <Route path="/parties" element={
          <PermissionRoute permission="Parties.Read" sectionName="Parties">
            <PartiesPage />
          </PermissionRoute>
        } />
        <Route
          path="/users"
          element={
            <AdminOnlyRoute>
              <UserManagementPage />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <AdminOnlyRoute>
              <SettingsPage />
            </AdminOnlyRoute>
          }
        />
        <Route
          path="/bilty-stationery"
          element={
            <PermissionRoute permission="InventoryIssue.Read" sectionName="Document Stationery">
              <BiltyStationeryPage />
            </PermissionRoute>
          }
        />
        <Route path="/inventory/enquiry" element={<PermissionRoute permission="InventoryIssue.Read" sectionName="Inventory Enquiry"><InventoryEnquiryPage /></PermissionRoute>} />
        <Route
          path="/audit-logs"
          element={
            <AdminOnlyRoute>
              <AuditLogPage />
            </AdminOnlyRoute>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to={accessToken ? "/" : "/login"} replace />} />
    </Routes>
  );
};
